import { useRef, useState } from "react";
import { useRecoilValue, useSetRecoilState } from "recoil";

import clsx from "clsx";

import styles from "./DateModal.module.css";

import { taskPopupState } from "../../../recoil/taskDetail/taskPopupState";

import { rRuleToText, textToRRule } from "../../../utils/taskDetail/recurrenceConverter";

import PropertyTitle from "../Common/PropertyTitle";
import RepeatAddModal from "./RepeatAddModal";

import { getBlockTypeFlags } from "../../../utils/common/block/getBlockTypeFlags";
import DateIcon from "../../../compoenets/atom/icons/DateIcon";
import { DefaultDurationValueMap } from "../../../types/account/enum";
import { usePreference } from "../../../hooks/preference/usePreference";
import { DefaultPreferenceOptions } from "../../../constants/userPreference";
import moment from "moment";
import { RepeatDropdownOptions } from "../../../types/block/enum";

import EmptyDateTimeButton from "./EmptyDateTimeButton";
import DateTimeControl from "./DateTimeControl";
import DateTimeMain from "./DateTimeMain";
import { DateModalProvider } from "./DateModalContext";

export default function DateModal({
  dateTimeStart,
  dateTimeEnd,

  setStartDateTime,
  setEndDateTime,

  isAllDay,
  setIsAllDay,

  handleDateClear,

  expand,
  repeatModalRef,
  repeatAddModalRef,

  // Picker states
  isTimeStartOpen,
  setIsTimeStartOpen,
  isTimeEndOpen,
  setIsTimeEndOpen,
  isDateStartOpen,
  setIsDateStartOpen,
  isDateEndOpen,
  setIsDateEndOpen,

  // Picker handlers
  onTimeStartChange,
  onTimeEndChange,
  onDateStartChange,
  onDateEndChange,
  timeFormat,
}) {
  const taskDetail = useRecoilValue(taskPopupState);
  const { preference: userPreference } = usePreference();
  const defaultDuration =
    DefaultDurationValueMap[
      userPreference?.defaultDuration ?? DefaultPreferenceOptions.defaultDuration
    ];
  const baseDuration = Number(defaultDuration) ?? 15;

  const [isRepeatAddModal, setIsRepeatAddModal] = useState(false);

  // custom repeat 설정여부
  const [isCustomRepeatSetted, setIsCustomRepeatSetted] = useState(false);

  const nowDate = new Date();
  const minutes = nowDate.getMinutes();
  const roundedMinutes = Math.ceil(minutes / baseDuration) * baseDuration;
  nowDate.setMinutes(roundedMinutes);
  const { data } = taskDetail;
  const { isTask } = getBlockTypeFlags(data.blockType);
  const setTaskDetail = useSetRecoilState(taskPopupState);

  const handleOnDelete = (event) => {
    event.stopPropagation();
    handleDateClear();
  };

  const syncTaskDetailOnAllDayChange = (startDateTime, endDateTime, isAllDay) => {
    const start = new Date(startDateTime);
    const end = new Date(endDateTime);

    setTaskDetail((prev) => ({
      ...prev,
      data: {
        ...prev.data,
        allDay: isAllDay,
        start: isAllDay ? moment(start).startOf("day") : start,
        end: isAllDay ? moment(end).add(1, "days").startOf("day") : end,
      },
    }));
  };

  const handleIsAllDay = (e) => {
    // 항상 e: React.ChangeEvent<HTMLInputElement> 타입으로 받음
    // e.target.checked는 바뀐 값을 반환.
    const newIsAllDay = e?.target?.checked;

    // 시간 설정용
    const baseStart = new Date(nowDate.getTime());
    const baseEnd = new Date(nowDate.getTime() + baseDuration * 60000);

    // 날짜 설정용
    const start = new Date(dateTimeStart ?? nowDate);
    const end = new Date(dateTimeEnd ?? nowDate);

    // NOTE 시간대로 변경된 경우에만 시/분 값 설정
    if (!newIsAllDay) {
      // isAllDay 대신 newIsAllDay 사용
      start.setHours(baseStart.getHours());
      start.setMinutes(baseStart.getMinutes());

      end.setHours(baseEnd.getHours());
      end.setMinutes(baseEnd.getMinutes());
    }

    setStartDateTime(start);
    setEndDateTime(end);
    setIsAllDay(newIsAllDay); // isAllDay 상태 업데이트

    // taskDetail 업데이트
    syncTaskDetailOnAllDayChange(start, end, newIsAllDay);
  };

  const handleAddDateTime = () => {
    const now = new Date();
    const minutes = now.getMinutes();
    const roundedMinutes = Math.ceil(minutes / baseDuration) * baseDuration;

    // 시작 시간 설정 (현재 시간에서 가장 가까운 15분 단위)
    const start = new Date(now);
    start.setMinutes(roundedMinutes);
    start.setSeconds(0);
    start.setMilliseconds(0);

    // 종료 시간 설정 (시작 시간 + baseDuration)
    const end = new Date(start);
    end.setMinutes(start.getMinutes() + baseDuration);

    setStartDateTime(start);
    setEndDateTime(end);

    setTaskDetail((prev) => ({
      ...prev,
      data: {
        ...prev.data,
        allDay: false,
        start: start,
        end: end,
      },
    }));
  };

  const handleRepeatChange = (value) => {
    // 선택된 값을 다시 선택하면 해제되도록 설정
    if (value === rRuleToText(taskDetail.data?.recurrence ?? "")) {
      setTaskDetail((prev) => ({
        ...prev,
        data: {
          ...prev.data,
          recurrence: null,
        },
      }));
      setIsRepeatAddModal(false);
      return;
    }

    // Custom Repeat 선택 시 반복 커스텀하는 모달 오픈
    // TS 변환시 as String 추가 필요
    if (value === RepeatDropdownOptions.NEW_REPEAT_SETTINGS) {
      setIsRepeatAddModal(true);
      return;
    }

    setTaskDetail((prev) => ({
      ...prev,
      data: {
        ...prev.data,
        recurrence: [textToRRule(value)],
      },
    }));
  };

  const contextValue = {
    // Date/Time values
    dateTimeStart,
    dateTimeEnd,
    isAllDay,
    timeFormat,

    // Setters
    setStartDateTime,
    setEndDateTime,
    setIsAllDay,

    // Picker states
    isTimeStartOpen,
    setIsTimeStartOpen,
    isTimeEndOpen,
    setIsTimeEndOpen,
    isDateStartOpen,
    setIsDateStartOpen,
    isDateEndOpen,
    setIsDateEndOpen,

    // Picker handlers
    onTimeStartChange,
    onTimeEndChange,
    onDateStartChange,
    onDateEndChange,

    // Actions
    handleDateClear,
    handleAddDateTime,
    handleIsAllDay,
    handleRepeatChange,
    handleOnDelete,

    // Refs
    repeatModalRef,
    repeatAddModalRef,

    // Task related
    recurrence: taskDetail.data?.recurrence ?? null,
    isTask,
    isNewBlock: taskDetail.isNewBlock ?? false,
  };

  return (
    <DateModalProvider value={contextValue}>
      <div className={styles.dateInputWrapper}>
        <div className={styles["setting-property-btn"]}>
          <PropertyTitle
            Icon={<DateIcon />}
            label="Date/Time"
            expand={expand}
          />

          <div
            id="date_property"
            className={clsx({
              [styles["date_property--expand"]]: expand,
              [styles["date_property"]]: !expand,
            })}
          >
            {dateTimeStart === null ? (
              <EmptyDateTimeButton />
            ) : (
              <>
                <DateTimeMain expand={expand} />
                <DateTimeControl />
              </>
            )}
          </div>
        </div>

        {isRepeatAddModal && (
          <RepeatAddModal
            isCustomRepeatSetted={isCustomRepeatSetted}
            setIsRepeatAddModal={setIsRepeatAddModal}
            setIsCustomRepeatSetted={setIsCustomRepeatSetted}
            repeatAddModalRef={repeatAddModalRef}
            dateStart={dateTimeStart}
          />
        )}
      </div>
    </DateModalProvider>
  );
}
